/**
 * Native Purchase Service - In-App Purchase Integration for iOS and Android
 * 
 * Connects native store purchases (StoreKit 2 / Google Play Billing)
 * to the centralized entitlement system.
 * 
 * PRODUCT IDENTIFIERS (to be configured in App Store Connect & Google Play Console):
 * - iOS: com.everydayworks.forgetmenot.premium.monthly ($2.99/mo)
 * - iOS: com.everydayworks.forgetmenot.family.monthly ($5.99/mo)
 * - Android: premium_monthly ($2.99/mo)
 * - Android: family_monthly ($5.99/mo)
 * 
 * SECURITY:
 * - All purchases are verified server-side before granting entitlements
 * - Frontend cannot self-grant subscription access
 * - Receipts/tokens are validated against Apple/Google servers
 */

import { Capacitor } from '@capacitor/core';

// Only import native purchases plugin when on native platforms
let NativePurchases = null;
if (Capacitor.isNativePlatform()) {
  import('@capgo/native-purchases').then(module => {
    NativePurchases = module.NativePurchases;
  }).catch(err => {
    console.warn('Native purchases plugin not available:', err);
  });
}

// =============================================================================
// PRODUCT CONFIGURATION
// =============================================================================

/**
 * Store Product IDs - MUST be configured via backend environment variables
 * 
 * The frontend fetches the available product IDs from the backend config endpoint.
 * This ensures product IDs are centrally managed and match store configuration.
 * 
 * DO NOT hardcode product IDs here - they come from:
 * - APPLE_PRODUCT_ID_PREMIUM_MONTHLY
 * - APPLE_PRODUCT_ID_FAMILY_MONTHLY
 * - GOOGLE_PRODUCT_ID_PREMIUM_MONTHLY
 * - GOOGLE_PRODUCT_ID_FAMILY_MONTHLY
 */

// Product configuration loaded from backend
let _productConfig = null;

/**
 * Load product configuration from backend
 */
export const loadProductConfig = async () => {
  if (_productConfig) return _productConfig;
  
  try {
    const { default: API } = await import('@/utils/api');
    const response = await API.get('/subscription/product-config');
    _productConfig = response.data;
    return _productConfig;
  } catch (error) {
    console.warn('Failed to load product config:', error);
    return null;
  }
};

/**
 * Get product IDs for a platform
 */
export const getProductIds = async (platform) => {
  const config = await loadProductConfig();
  if (!config) return [];
  return config[platform] ? Object.keys(config[platform]) : [];
};

/**
 * Get the store product ID for a plan
 */
export const getStoreProductId = async (planId, platform) => {
  const config = await loadProductConfig();
  if (!config || !config[platform]) return null;
  
  // Find the product ID that maps to this plan
  for (const [productId, planConfig] of Object.entries(config[platform])) {
    if (planConfig.plan_id === planId) {
      return productId;
    }
  }
  return null;
};

/**
 * Get the tier for a product ID
 */
export const getTierFromProductId = async (productId, platform) => {
  const config = await loadProductConfig();
  if (!config || !config[platform]) return null;
  
  const productConfig = config[platform][productId];
  return productConfig?.tier || null;
};

// =============================================================================
// PURCHASE STATE MANAGEMENT
// =============================================================================

/**
 * Purchase states for UI feedback
 */
export const PurchaseState = {
  IDLE: 'idle',
  LOADING_PRODUCTS: 'loading_products',
  PRODUCTS_LOADED: 'products_loaded',
  PURCHASING: 'purchasing',
  VERIFYING: 'verifying',
  SUCCESS: 'success',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
  RESTORING: 'restoring',
  RESTORE_SUCCESS: 'restore_success',
  RESTORE_EMPTY: 'restore_empty',
  OFFLINE: 'offline',
  STORE_UNAVAILABLE: 'store_unavailable',
};

/**
 * Internal state for purchase service
 */
let _state = {
  initialized: false,
  platform: null,
  products: [],
  purchaseState: PurchaseState.IDLE,
  lastError: null,
  isPurchasing: false, // Prevents duplicate purchase requests
  diagnostics: [],
};

// =============================================================================
// PLATFORM DETECTION
// =============================================================================

/**
 * Get current platform
 */
export const getPlatform = () => {
  if (Capacitor.getPlatform() === 'ios') return 'ios';
  if (Capacitor.getPlatform() === 'android') return 'android';
  return 'web';
};

/**
 * Check if native purchases are supported on current platform
 */
export const isNativePurchaseSupported = () => {
  const platform = getPlatform();
  return platform === 'ios' || platform === 'android';
};

/**
 * Check if we're on iOS
 */
export const isIOS = () => getPlatform() === 'ios';

/**
 * Check if we're on Android
 */
export const isAndroid = () => getPlatform() === 'android';

/**
 * Check if we're on web
 */
export const isWeb = () => getPlatform() === 'web';

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize the purchase service
 * Must be called before any purchase operations
 */
export const initializePurchases = async () => {
  if (_state.initialized) {
    logDiagnostic('Already initialized');
    return { success: true };
  }

  const platform = getPlatform();
  _state.platform = platform;

  // Web platform uses Stripe checkout
  if (platform === 'web') {
    _state.initialized = true;
    logDiagnostic('Initialized for web platform (using Stripe)');
    return { success: true, platform: 'web' };
  }

  // Native platforms use StoreKit/Google Play
  if (!NativePurchases) {
    // Try to load the plugin again
    try {
      const module = await import('@capgo/native-purchases');
      NativePurchases = module.NativePurchases;
    } catch (err) {
      logDiagnostic('Failed to load native purchases plugin', err);
      return { success: false, error: 'Native purchases not available' };
    }
  }

  try {
    _state.purchaseState = PurchaseState.LOADING_PRODUCTS;
    
    // Load product configuration from backend
    const config = await loadProductConfig();
    if (!config) {
      throw new Error('Unable to load product configuration');
    }
    
    // Get product IDs for current platform from config
    const productIds = config[platform] ? Object.keys(config[platform]) : [];
    
    if (productIds.length === 0) {
      logDiagnostic(`No product IDs configured for platform: ${platform}`);
      _state.purchaseState = PurchaseState.STORE_UNAVAILABLE;
      return { 
        success: false, 
        error: 'Subscriptions not configured. Please try again later.',
        notConfigured: true 
      };
    }

    // Fetch products from store
    const result = await NativePurchases.getProducts({ productIds });
    
    if (result.products && result.products.length > 0) {
      _state.products = result.products;
      _state.purchaseState = PurchaseState.PRODUCTS_LOADED;
      _state.initialized = true;
      logDiagnostic(`Loaded ${result.products.length} products from ${platform} store`);
      return { success: true, products: result.products };
    } else {
      _state.purchaseState = PurchaseState.STORE_UNAVAILABLE;
      logDiagnostic('No products returned from store');
      return { success: false, error: 'No products available' };
    }
  } catch (error) {
    _state.purchaseState = PurchaseState.FAILED;
    _state.lastError = error.message;
    logDiagnostic('Initialization failed', error);
    
    // Check for specific error types
    if (error.message?.includes('network') || error.message?.includes('offline')) {
      _state.purchaseState = PurchaseState.OFFLINE;
      return { success: false, error: 'Network unavailable', offline: true };
    }
    
    return { success: false, error: error.message };
  }
};

// =============================================================================
// PRODUCT INFORMATION
// =============================================================================

/**
 * Get loaded products
 */
export const getProducts = () => {
  return _state.products;
};

/**
 * Get a specific product by internal plan ID
 */
export const getProductByPlanId = async (planId) => {
  const platform = getPlatform();
  const productId = await getStoreProductId(planId, platform);
  
  if (!productId) return null;
  
  return _state.products.find(p => p.productId === productId || p.id === productId);
};

/**
 * Get formatted price for a product
 */
export const getProductPrice = (planId) => {
  const product = getProductByPlanId(planId);
  if (!product) return null;
  
  // Return localized price from store
  return product.localizedPrice || product.price || null;
};

// =============================================================================
// PURCHASE FLOW
// =============================================================================

/**
 * Start a purchase for a plan
 * 
 * @param {string} planId - The internal plan ID (e.g., 'premium_monthly')
 * @param {string} userId - The user's ID for backend verification
 * @param {boolean} isDemo - Whether we're in demo mode
 * @returns {Promise<{success: boolean, error?: string, tier?: string}>}
 */
export const purchasePlan = async (planId, userId, isDemo = false) => {
  // CRITICAL: Never allow purchases in demo mode
  if (isDemo) {
    logDiagnostic('Purchase blocked - demo mode active');
    return { 
      success: false, 
      error: 'Purchases are not available in demo mode. Please create an account first.',
      blocked: 'demo_mode'
    };
  }

  // Prevent duplicate purchase requests
  if (_state.isPurchasing) {
    logDiagnostic('Purchase blocked - another purchase in progress');
    return { success: false, error: 'A purchase is already in progress' };
  }

  const platform = getPlatform();

  // Web platform uses Stripe
  if (platform === 'web') {
    return purchaseViaStripe(planId, userId);
  }

  // Native platforms use StoreKit/Google Play
  if (!_state.initialized) {
    const initResult = await initializePurchases();
    if (!initResult.success) {
      return { success: false, error: initResult.error };
    }
  }

  // Get product ID from backend configuration
  const productId = await getStoreProductId(planId, platform);
  if (!productId) {
    logDiagnostic(`Product ID not found for plan: ${planId} on ${platform}`);
    return { success: false, error: 'This subscription is not available. Please try again later.' };
  }

  try {
    _state.isPurchasing = true;
    _state.purchaseState = PurchaseState.PURCHASING;
    logDiagnostic(`Starting purchase for ${planId} (${productId})`);

    // Initiate purchase through native plugin
    const purchaseResult = await NativePurchases.purchaseProduct({
      productId,
      // Include user ID for server verification
      applicationUsername: userId,
    });

    if (!purchaseResult || purchaseResult.cancelled) {
      _state.purchaseState = PurchaseState.CANCELLED;
      logDiagnostic('Purchase cancelled by user');
      return { success: false, cancelled: true };
    }

    // Purchase completed - now verify with backend (REQUIRED)
    // SECURITY: Never grant access based on client purchase status alone
    _state.purchaseState = PurchaseState.VERIFYING;
    logDiagnostic('Purchase completed, verifying with backend...');

    const verifyResult = await verifyPurchaseWithBackend({
      platform,
      productId,
      planId,
      receipt: purchaseResult.receipt || purchaseResult.transactionReceipt,
      transactionId: purchaseResult.transactionId,
      originalTransactionId: purchaseResult.originalTransactionId,
      purchaseToken: purchaseResult.purchaseToken, // Android
    });

    if (verifyResult.success) {
      _state.purchaseState = PurchaseState.SUCCESS;
      logDiagnostic('Purchase verified successfully', verifyResult);
      return {
        success: true,
        tier: verifyResult.tier,
        expiresAt: verifyResult.expiresAt,
      };
    } else {
      _state.purchaseState = PurchaseState.FAILED;
      logDiagnostic('Purchase verification failed', verifyResult);
      return { success: false, error: verifyResult.error || 'Verification failed' };
    }
  } catch (error) {
    _state.purchaseState = PurchaseState.FAILED;
    _state.lastError = error.message;
    logDiagnostic('Purchase error', error);

    // Handle specific error types
    if (error.code === 'E_USER_CANCELLED' || error.message?.includes('cancelled')) {
      _state.purchaseState = PurchaseState.CANCELLED;
      return { success: false, cancelled: true };
    }
    
    if (error.message?.includes('network') || error.message?.includes('offline')) {
      _state.purchaseState = PurchaseState.OFFLINE;
      return { success: false, error: 'Network unavailable. Please try again.', offline: true };
    }

    if (error.message?.includes('declined') || error.message?.includes('payment')) {
      return { success: false, error: 'Payment was declined. Please try again or use a different payment method.' };
    }

    return { success: false, error: 'Purchase failed. Please try again.' };
  } finally {
    _state.isPurchasing = false;
  }
};

/**
 * Purchase via Stripe (web platform)
 */
const purchaseViaStripe = async (planId, userId) => {
  try {
    _state.purchaseState = PurchaseState.PURCHASING;
    
    const { authApi } = await import('./authService');
    const response = await authApi.post('/api/subscription/checkout', {
      plan_id: planId,
      origin_url: window.location.origin,
    });

    if (response.data.checkout_url) {
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      return { success: true, redirecting: true };
    }

    return { success: false, error: 'Failed to create checkout session' };
  } catch (error) {
    _state.purchaseState = PurchaseState.FAILED;
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

// =============================================================================
// PURCHASE VERIFICATION
// =============================================================================

/**
 * Verify purchase with backend
 * SECURITY: This is the critical step - backend validates receipt with Apple/Google
 */
const verifyPurchaseWithBackend = async (purchaseData) => {
  try {
    const { authApi } = await import('./authService');
    
    const response = await authApi.post('/api/subscription/verify-native-purchase', {
      platform: purchaseData.platform,
      product_id: purchaseData.productId,
      plan_id: purchaseData.planId,
      receipt: purchaseData.receipt,
      transaction_id: purchaseData.transactionId,
      original_transaction_id: purchaseData.originalTransactionId,
      purchase_token: purchaseData.purchaseToken,
    });

    return response.data;
  } catch (error) {
    console.error('Backend verification failed:', error);
    return { 
      success: false, 
      error: error.response?.data?.detail || 'Verification failed' 
    };
  }
};

// =============================================================================
// RESTORE PURCHASES
// =============================================================================

/**
 * Restore previous purchases
 * Required for App Store compliance
 */
export const restorePurchases = async (userId) => {
  const platform = getPlatform();

  if (platform === 'web') {
    // Web uses account-based subscription, no restore needed
    return { success: true, message: 'Your subscription is linked to your account.' };
  }

  if (!NativePurchases) {
    return { success: false, error: 'Store not available' };
  }

  try {
    _state.purchaseState = PurchaseState.RESTORING;
    logDiagnostic('Restoring purchases...');

    const result = await NativePurchases.restorePurchases();

    if (!result.purchases || result.purchases.length === 0) {
      _state.purchaseState = PurchaseState.RESTORE_EMPTY;
      logDiagnostic('No purchases to restore');
      return { success: true, empty: true, message: 'No previous purchases found.' };
    }

    // Verify each restored purchase with backend
    let restoredTier = null;
    for (const purchase of result.purchases) {
      const verifyResult = await verifyPurchaseWithBackend({
        platform,
        productId: purchase.productId,
        planId: mapProductToPlan(purchase.productId, platform),
        receipt: purchase.receipt || purchase.transactionReceipt,
        transactionId: purchase.transactionId,
        originalTransactionId: purchase.originalTransactionId,
        purchaseToken: purchase.purchaseToken,
        isRestore: true,
      });

      if (verifyResult.success && verifyResult.tier) {
        // Keep the highest tier
        if (!restoredTier || tierLevel(verifyResult.tier) > tierLevel(restoredTier)) {
          restoredTier = verifyResult.tier;
        }
      }
    }

    if (restoredTier) {
      _state.purchaseState = PurchaseState.RESTORE_SUCCESS;
      logDiagnostic('Purchases restored successfully', { tier: restoredTier });
      return { success: true, tier: restoredTier };
    } else {
      _state.purchaseState = PurchaseState.RESTORE_EMPTY;
      return { success: true, empty: true, message: 'No active subscriptions found.' };
    }
  } catch (error) {
    _state.purchaseState = PurchaseState.FAILED;
    logDiagnostic('Restore failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Map product ID to plan ID using backend config
 */
const mapProductToPlan = async (productId, platform) => {
  const config = await loadProductConfig();
  if (!config || !config[platform]) return null;
  
  const productConfig = config[platform][productId];
  return productConfig?.plan_id || null;
};

/**
 * Get tier level for comparison
 */
const tierLevel = (tier) => {
  const levels = { free: 0, premium: 1, family: 2 };
  return levels[tier] || 0;
};

// =============================================================================
// SUBSCRIPTION MANAGEMENT
// =============================================================================

/**
 * Open platform-specific subscription management
 */
export const openSubscriptionManagement = async () => {
  const platform = getPlatform();

  if (platform === 'ios') {
    // Open iOS subscription settings
    // This will open the App Store subscription management page
    try {
      if (NativePurchases?.manageSubscriptions) {
        await NativePurchases.manageSubscriptions();
      } else {
        // Fallback: open App Store settings URL
        window.open('https://apps.apple.com/account/subscriptions', '_blank');
      }
      return { success: true };
    } catch (error) {
      // Fallback to URL
      window.open('https://apps.apple.com/account/subscriptions', '_blank');
      return { success: true };
    }
  } else if (platform === 'android') {
    // Open Google Play subscription management
    try {
      if (NativePurchases?.manageSubscriptions) {
        await NativePurchases.manageSubscriptions();
      } else {
        // Fallback: open Play Store subscriptions URL
        window.open('https://play.google.com/store/account/subscriptions', '_blank');
      }
      return { success: true };
    } catch (error) {
      window.open('https://play.google.com/store/account/subscriptions', '_blank');
      return { success: true };
    }
  } else {
    // Web: navigate to account settings
    return { success: false, redirect: '/settings/subscription' };
  }
};

/**
 * Check if user has an active subscription
 * This should be called on app launch after verifying with backend
 */
export const syncSubscriptionStatus = async () => {
  try {
    const { authApi } = await import('./authService');
    const response = await authApi.get('/api/subscription/status');
    return response.data;
  } catch (error) {
    console.error('Failed to sync subscription status:', error);
    return null;
  }
};

// =============================================================================
// UPGRADE FLOW
// =============================================================================

/**
 * Upgrade from Premium to Family
 */
export const upgradePremiumToFamily = async (userId, isDemo = false) => {
  if (isDemo) {
    return { success: false, error: 'Upgrades not available in demo mode' };
  }

  const platform = getPlatform();

  if (platform === 'web') {
    // For web, create a new checkout for family plan
    return purchaseViaStripe('family_monthly', userId);
  }

  // For native platforms, the store handles upgrades automatically
  // When user purchases Family while on Premium, the store will:
  // - Prorate the remaining Premium subscription
  // - Start the Family subscription
  return purchasePlan('family_monthly', userId, isDemo);
};

// =============================================================================
// DIAGNOSTICS
// =============================================================================

/**
 * Log diagnostic information (internal only)
 */
const logDiagnostic = (message, data = null) => {
  const entry = {
    timestamp: new Date().toISOString(),
    message,
    data: data ? JSON.stringify(data).substring(0, 500) : null,
  };
  
  _state.diagnostics.push(entry);
  
  // Keep only last 50 entries
  if (_state.diagnostics.length > 50) {
    _state.diagnostics = _state.diagnostics.slice(-50);
  }
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[NativePurchases] ${message}`, data || '');
  }
};

/**
 * Get diagnostic logs (for debugging, not exposed to users)
 */
export const getDiagnostics = () => {
  return {
    platform: _state.platform,
    initialized: _state.initialized,
    purchaseState: _state.purchaseState,
    productsLoaded: _state.products.length,
    logs: _state.diagnostics,
  };
};

/**
 * Get current purchase state for UI
 */
export const getPurchaseState = () => _state.purchaseState;

/**
 * Reset purchase state to idle
 */
export const resetPurchaseState = () => {
  _state.purchaseState = PurchaseState.IDLE;
  _state.lastError = null;
};

/**
 * Get last error message
 */
export const getLastError = () => _state.lastError;

// =============================================================================
// CLEANUP
// =============================================================================

/**
 * Cleanup when component unmounts or app closes
 */
export const cleanup = () => {
  _state.isPurchasing = false;
  _state.purchaseState = PurchaseState.IDLE;
};

export default {
  // Platform detection
  getPlatform,
  isNativePurchaseSupported,
  isIOS,
  isAndroid,
  isWeb,
  
  // Initialization
  initializePurchases,
  
  // Products
  getProducts,
  getProductByPlanId,
  getProductPrice,
  
  // Purchase flow
  purchasePlan,
  restorePurchases,
  upgradePremiumToFamily,
  
  // Subscription management
  openSubscriptionManagement,
  syncSubscriptionStatus,
  
  // State
  getPurchaseState,
  resetPurchaseState,
  getLastError,
  
  // Diagnostics
  getDiagnostics,
  
  // Cleanup
  cleanup,
  
  // Constants
  PurchaseState,
  
  // Config loaders
  loadProductConfig,
  getProductIds,
  getStoreProductId,
  getTierFromProductId,
};
